import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Heart,
    ChevronRight,
    Home,
    Shield,
    Compass,
    Eye,
    Clock,
    Feather,
    Star,
    Check,
    ArrowLeft
} from 'lucide-react';
import { PrimaryButton, SecondaryButton, TertiaryButton } from '../components/brand/BrandButton';
import { BrandCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { ModuleHeader, SafetyFooter } from '../components/brand/QuickExit';
import { PersistentBottomBar, BottomBarSpacer } from '../components/brand/PersistentBottomBar';

const AttachmentNeeds = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState('intro'); // intro, needs, reflection, guidance, closing
    const [selectedNeeds, setSelectedNeeds] = useState([]);
    const [isUnsure, setIsUnsure] = useState(false);

    const attachmentNeeds = [
        {
            id: 'reassurance',
            name: 'Reassurance',
            description: 'I need to know we\'re okay.',
            icon: Shield,
            variant: 'sage',
            guidance: 'Try offering yourself a soft reminder: "We\'re okay. I\'m safe."'
        },
        {
            id: 'closeness',
            name: 'Closeness',
            description: 'I need to feel connected to you.',
            icon: Heart,
            variant: 'rose',
            guidance: 'Try reaching for a small moment of connection — a touch, a glance, a word.'
        },
        {
            id: 'space',
            name: 'Space with connection',
            description: 'I need room to breathe without losing us.',
            icon: Compass,
            variant: 'blue',
            guidance: 'Try taking intentional space with a gentle check-in: "I need a moment, but I\'m still here."'
        },
        {
            id: 'understanding',
            name: 'Understanding',
            description: 'I need you to get where I\'m coming from.',
            icon: Eye,
            variant: 'lavender',
            guidance: 'Try naming what you\'re feeling out loud, even just to yourself. Being understood starts with being seen.'
        },
        {
            id: 'predictability',
            name: 'Predictability',
            description: 'I need to know what to expect.',
            icon: Clock,
            variant: 'gold',
            guidance: 'Try noticing what feels uncertain right now. Naming it can help it feel more manageable.'
        },
        {
            id: 'warmth',
            name: 'Warmth',
            description: 'I need gentleness and softness.',
            icon: Feather,
            variant: 'rose',
            guidance: 'Try softening your shoulders and speaking gently to yourself: "I deserve tenderness."'
        },
        {
            id: 'significance',
            name: 'Significance',
            description: 'I need to feel important to you.',
            icon: Star,
            variant: 'gold',
            guidance: 'Try reminding yourself: "My feelings matter. My presence matters."'
        }
    ];

    const toggleNeed = (needId) => {
        setIsUnsure(false);
        setSelectedNeeds(prev => 
            prev.includes(needId) 
                ? prev.filter(id => id !== needId)
                : [...prev, needId]
        );
    };

    const handleUnsure = () => {
        setIsUnsure(true);
        setSelectedNeeds([]);
    };

    const getSelectedNeedsData = () => {
        return attachmentNeeds.filter(need => selectedNeeds.includes(need.id));
    };

    const handleBackNavigation = () => {
        if (step === 'intro') {
            navigate('/relationship-hub');
        } else if (step === 'needs') {
            setStep('intro');
        } else if (step === 'reflection') {
            setStep('needs');
        } else if (step === 'guidance') {
            setStep('reflection');
        }
    };

    return (
        <div className="min-h-screen bg-transparent">
            {/* Orenda Module Header */}
            <ModuleHeader
                title="Attachment Needs"
                onBack={handleBackNavigation}
                backDestination="/relationship-hub"
                exitDestination="/home?tab=relationships"
                showBack={step !== 'closing'}
            />

            <main className="max-w-xl mx-auto px-6 py-8">
                {/* INTRO */}
                {step === 'intro' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-10">
                            <IconContainer variant="rose" size="xl" useGradient={true} className="mx-auto mb-6">
                                <Heart className="w-8 h-8" />
                            </IconContainer>
                            
                            <BodyText className="text-[var(--orenda-text-secondary)] max-w-md mx-auto leading-relaxed mb-3">
                                Attachment needs are gentle emotional needs that help us feel safe, connected, and understood.
                            </BodyText>
                            
                            <SmallText className="text-[var(--orenda-text-muted)] max-w-sm mx-auto leading-relaxed">
                                These needs are normal, human, and valid.
                            </SmallText>
                        </div>
                        
                        <div className="mt-10 text-center">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                Take your time with this.
                            </SmallText>
                        </div>
                        
                        <BottomBarSpacer height="default" />
                    </div>
                )}
                
                {/* Persistent Bottom Bar for Intro */}
                {step === 'intro' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => setStep('needs')}
                            className="w-full"
                            size="action"
                            data-testid="explore-btn"
                        >
                            Explore
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                    </PersistentBottomBar>
                )}

                {/* COMMON ATTACHMENT NEEDS */}
                {step === 'needs' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-8">
                            <Heading as="h2" size="text-xl" className="mb-2">
                                Common Attachment Needs
                            </Heading>
                            <SmallText className="text-[var(--orenda-text-muted)]">
                                These are gentle needs that help us feel safe and connected.
                            </SmallText>
                        </div>

                        <div className="space-y-3 mb-8">
                            {attachmentNeeds.map((need, index) => (
                                <div className="glass-card rounded-2xl p-5"
                                    key={need.id}
                                    variant="default"
                                    padding="lg"
                                    className="animate-fade-in"
                                    style={{ animationDelay: `${index * 50}ms` }}
                                    data-testid={`need-card-${need.id}`}
                                >
                                    <div className="flex items-start gap-4">
                                        <IconContainer variant={need.variant} size="md" useGradient={true} className="flex-shrink-0 mt-0.5">
                                            <need.icon className="w-4 h-4" />
                                        </IconContainer>
                                        <div>
                                            <Heading as="h3" size="text-base" className="mb-1">
                                                {need.name}
                                            </Heading>
                                            <SmallText className="text-[var(--orenda-text-secondary)] italic">
                                                "{need.description}"
                                            </SmallText>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <PrimaryButton
                            onClick={() => setStep('reflection')}
                            className="w-full"
                            size="lg"
                        >
                            Continue to Reflection
                            <ChevronRight className="w-4 h-4 ml-2" />
                        </PrimaryButton>

                        <div className="mt-6 text-center">
                            <TertiaryButton onClick={handleBackNavigation}>
                                <ArrowLeft className="w-4 h-4 mr-1" />
                                Back
                            </TertiaryButton>
                        </div>
                    </div>
                )}

                {/* REFLECTION */}
                {step === 'reflection' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-8">
                            <Heading as="h2" size="text-xl" className="mb-2">
                                Which of these needs feels most alive for you right now?
                            </Heading>
                            <SmallText className="text-[var(--orenda-text-muted)]">
                                Select one or more that resonate with you.
                            </SmallText>
                        </div>

                        <div className="space-y-3 mb-6">
                            {attachmentNeeds.map((need, index) => {
                                const isSelected = selectedNeeds.includes(need.id);
                                return (
                                    <button
                                        key={need.id}
                                        onClick={() => toggleNeed(need.id)}
                                        className={`w-full p-4 rounded-2xl text-left border-2 transition-all duration-200
                                            ${isSelected 
                                                ? 'border-[#1D6B6B] bg-[var(--orenda-soft-teal)]/10' 
                                                : 'border-[var(--orenda-border)] bg-[var(--orenda-soft-white)] hover:bg-[var(--orenda-cream)] hover:border-[var(--orenda-border-hover)]'
                                            } animate-fade-in`}
                                        style={{ animationDelay: `${index * 50}ms` }}
                                        data-testid={`select-need-${need.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                                                isSelected ? 'bg-[#1D6B6B]' : 'bg-[var(--orenda-border)]'
                                            }`}>
                                                {isSelected && <Check className="w-4 h-4 text-white" />}
                                            </div>
                                            <div>
                                                <BodyText className="font-medium">{need.name}</BodyText>
                                                <SmallText className="text-[var(--orenda-text-muted)]">{need.description}</SmallText>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <button
                            onClick={handleUnsure}
                            className={`w-full p-4 rounded-2xl text-left border-2 transition-all duration-200 mb-8
                                ${isUnsure 
                                    ? 'border-[#1D6B6B] bg-[var(--orenda-soft-teal)]/10' 
                                    : 'border-[var(--orenda-border)] bg-[var(--orenda-soft-white)] hover:bg-[var(--orenda-cream)] hover:border-[var(--orenda-border-hover)]'
                                }`}
                            data-testid="select-unsure"
                        >
                            <div className="flex items-center gap-4">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                                    isUnsure ? 'bg-[#1D6B6B]' : 'bg-[var(--orenda-border)]'
                                }`}>
                                    {isUnsure && <Check className="w-4 h-4 text-white" />}
                                </div>
                                <BodyText className="font-medium">I'm not sure</BodyText>
                            </div>
                        </button>

                        {isUnsure && (
                            <div className="glass-card rounded-2xl p-5">
                                <SmallText className="text-[var(--orenda-text-secondary)] text-center italic">
                                    It's okay not to know. Needs become clearer when the nervous system settles.
                                </SmallText>
                            </div>
                        )}

                        <PrimaryButton
                            onClick={() => setStep(selectedNeeds.length > 0 ? 'guidance' : 'closing')}
                            className="w-full"
                            size="lg"
                            disabled={!isUnsure && selectedNeeds.length === 0}
                        >
                            Continue
                            <ChevronRight className="w-4 h-4 ml-2" />
                        </PrimaryButton>

                        <div className="mt-6 text-center">
                            <TertiaryButton onClick={handleBackNavigation}>
                                <ArrowLeft className="w-4 h-4 mr-1" />
                                Back
                            </TertiaryButton>
                        </div>
                    </div>
                )}

                {/* GUIDANCE */}
                {step === 'guidance' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-8">
                            <Heading as="h2" size="text-xl" className="mb-2">
                                What might help
                            </Heading>
                            <SmallText className="text-[var(--orenda-text-muted)]">
                                Gentle guidance for your selected needs.
                            </SmallText>
                        </div>

                        <div className="space-y-4 mb-8">
                            {getSelectedNeedsData().map((need, index) => (
                                <div className="glass-card rounded-2xl p-5"
                                    key={need.id}
                                    variant="elevated"
                                    padding="lg"
                                    className="animate-fade-in"
                                    style={{ animationDelay: `${index * 100}ms` }}
                                >
                                    <div className="flex items-start gap-4 mb-4">
                                        <IconContainer variant={need.variant} size="sm" useGradient={true} className="flex-shrink-0">
                                            <need.icon className="w-3 h-3" />
                                        </IconContainer>
                                        <Heading as="h3" size="text-base">
                                            {need.name}
                                        </Heading>
                                    </div>
                                    <div className="p-4 rounded-xl bg-[var(--orenda-soft-teal)]/10 border border-[var(--orenda-soft-teal)]/20">
                                        <SmallText className="text-[var(--orenda-text-secondary)] leading-relaxed">
                                            {need.guidance}
                                        </SmallText>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <PrimaryButton
                            onClick={() => setStep('closing')}
                            className="w-full"
                            size="lg"
                        >
                            Continue
                            <ChevronRight className="w-4 h-4 ml-2" />
                        </PrimaryButton>

                        <div className="mt-6 text-center">
                            <TertiaryButton onClick={handleBackNavigation}>
                                <ArrowLeft className="w-4 h-4 mr-1" />
                                Back
                            </TertiaryButton>
                        </div>
                    </div>
                )}

                {/* CLOSING */}
                {step === 'closing' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-6">
                            <IconContainer variant="sage" size="xl" useGradient={true} className="mx-auto mb-4">
                                <Heart className="w-6 h-6" />
                            </IconContainer>
                        </div>

                        <div className="glass-card-sage rounded-2xl p-6 mb-6">
                            <BodyText className="text-[var(--orenda-text-primary)] text-center leading-relaxed text-lg">
                                Your needs make sense. Naming them is a gentle act of self-understanding.
                            </BodyText>
                        </div>
                        
                        <div className="text-center mb-8">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                Notice how it feels to name these needs. You don't have to do anything with them yet.
                            </SmallText>
                        </div>

                        <BottomBarSpacer height="extended" />
                    </div>
                )}
                
                {/* Persistent Bottom Bar for Closing */}
                {step === 'closing' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => navigate('/relationship-hub')}
                            className="w-full"
                            size="action"
                            data-testid="return-to-hub-btn"
                        >
                            <Home className="w-4 h-4 mr-1.5" />
                            Return to Hub
                        </PrimaryButton>
                        <SecondaryButton
                            onClick={() => {
                                setStep('intro');
                                setSelectedNeeds([]);
                                setIsUnsure(false);
                            }}
                            className="w-full"
                            size="action"
                        >
                            Explore again
                        </SecondaryButton>
                    </PersistentBottomBar>
                )}

                {/* Safety Footer */}
                {step !== 'closing' && step !== 'intro' && (
                    <SafetyFooter
                        destination="/relationship-hub"
                        exitLabel="Come back later"
                        reassurance="You can pause anytime."
                    />
                )}
            </main>
        </div>
    );
};

export default AttachmentNeeds;
